import React, { useState } from 'react';
import Button from '@/Components/Button';
import { cn } from '@/utils';
import IconAdd from '@/svgs/add.svg?react';
import IconCode from '@/svgs/code-box.svg?react';
import IconPackage from '@/svgs/folder.svg?react';
import IconChat from '@/svgs/chat.svg?react';
import useRoute from '@/Hooks/useRoute';
import { Tooltip } from '@/Components/Tooltip';
import Popover from '@/Components/Popover';
import useTypedPage from '@/Hooks/useTypedPage';
import { router } from '@inertiajs/react';

export default function ButtonCreateFile({
  route: routeParam,
}: {
  route: string;
}) {
  const [isOptionsPopoverOpen, setIsOptionsPopoverOpen] = useState(false);
  const route = useRoute();
  const { auth } = useTypedPage().props;
  function handleClick(e: React.MouseEvent) {
    if (auth.user) {
      // if there is an authenticated user, then show popover
      e.preventDefault();
      setIsOptionsPopoverOpen(true);
    }
  }

  function closePopover() {
    setIsOptionsPopoverOpen(false);
  }

  function openConversation() {
    router.post(
      route('conversations.store'),
      {},
      {
        preserveState: false,
      }
    );
  }

  function handleNewCodeClick() {
    router.visit(route('files.create'));
  }

  return (
    <Tooltip
      message="Start a chat or create a new snippet"
      content={{ side: 'right' }}
    >
      <>
        <Button
          href={routeParam}
          onClick={handleClick}
          className={cn(
            'group w-full bg-brand-50 text-brand-dark-100 dark:bg-brand-dark-25 dark:text-brand-light-100 dark:hover:text-brand-dark-100',
            {
              'bg-transparent': route().current('files.create'),
            }
          )}
          proxy_enabled={true}
          forceUrlDomain="app"
        >
          <IconCode className="h-6 w-6 text-brand group-hover:text-brand-dark-75 dark:text-brand-dark" />
          <span className="collapsable">Create New</span>
          <IconAdd className="collapsable ml-auto h-6 w-6" />
        </Button>
        <Popover
          open={isOptionsPopoverOpen}
          style={{
            width: 300,
            zIndex: 1,
          }}
          side="right"
          sideOffset={25}
          align="end"
          alignOffset={-160}
          closeAction={closePopover}
          content={
            <div
              className={cn(
                'popover-content-left flex flex-col divide-y divide-gray-800 rounded-xl',
                'bg-brand-25 text-brand-dark-25 dark:bg-brand-dark-75 dark:text-brand-dark-25'
              )}
            >
              <div className="flex flex-col gap-5 p-3">
                <div
                  onClick={handleNewCodeClick}
                  className="cursor-pointer rounded p-3 hover:dark:bg-brand-dark-50"
                >
                  <p className="flex items-center gap-2 text-brand-dark-100/40 dark:text-brand-light-75">
                    <IconPackage className="h-6 w-6 text-brand dark:text-brand-dark" />
                    New Code
                  </p>
                  <div className="mt-1 flex items-center">
                    <p className="text-brand-dark-100/40 dark:text-brand-light-25">
                      Create a single file, bundle of related snippets or
                      WordPress plugin
                    </p>
                  </div>
                </div>
                <div
                  onClick={openConversation}
                  className="cursor-pointer rounded p-3 hover:dark:bg-brand-dark-50"
                >
                  <p className="flex items-center gap-2 text-brand-dark-100/40 dark:text-brand-light-75">
                    <IconChat className="h-6 w-6 text-brand dark:text-brand-dark" />
                    New Chat
                  </p>
                  <div className="mt-1 flex items-center">
                    <p className="text-brand-dark-100/40 dark:text-brand-light-25">
                      Conversation only mode
                    </p>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </>
    </Tooltip>
  );
}
